import { Link } from "react-router";
import {
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalClose,
  ModalBody,
  ModalFooter,
} from "@mieweb/ui";
import { Sparkles } from "lucide-react";
import { changelog, type ChangelogEntry } from "../data/changelog";
import { APP_VERSION } from "../lib/build-info";

/** How many releases to show in the modal before pointing at /changelog. */
const MAX_ENTRIES = 5;

const TAG_STYLES = {
  feature:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  improvement:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  fix: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
} as const;

const TAG_LABELS = {
  feature: "New",
  improvement: "Improved",
  fix: "Fixed",
} as const;

function EntryList({
  type,
  items,
}: {
  type: keyof typeof TAG_STYLES;
  items?: string[];
}) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span
            className={`mt-0.5 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${TAG_STYLES[type]}`}
          >
            {TAG_LABELS[type]}
          </span>
          <span className="text-muted-foreground">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Entry({ entry }: { entry: ChangelogEntry }) {
  return (
    <article className="space-y-3">
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">
          {entry.title}
        </h3>
        <Badge
          variant={entry.version === APP_VERSION ? "success" : "secondary"}
          size="sm"
        >
          v{entry.version}
        </Badge>
        <span className="text-xs text-muted-foreground">{entry.date}</span>
      </header>
      {entry.description && (
        <p className="text-sm text-muted-foreground">{entry.description}</p>
      )}
      <EntryList type="feature" items={entry.features} />
      <EntryList type="improvement" items={entry.improvements} />
      <EntryList type="fix" items={entry.fixes} />
    </article>
  );
}

/**
 * The "What's New" reader — recent release notes in a modal. Opened from the
 * sidebar update badge, and automatically once per release. When `onReload`
 * is provided (a newer web build is live), the footer offers a one-click
 * reload into the new version.
 */
export function WhatsNewModal({
  open,
  onClose,
  onReload,
}: {
  open: boolean;
  onClose: () => void;
  onReload?: () => void;
}) {
  const entries = changelog.slice(0, MAX_ENTRIES);
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <ModalHeader>
        <ModalTitle>
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            What&apos;s new in Pollenate
          </span>
        </ModalTitle>
        <ModalClose />
      </ModalHeader>
      <ModalBody>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No release notes yet — check back soon.
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-8 overflow-y-auto pr-1">
            {entries.map((entry) => (
              <Entry key={entry.version} entry={entry} />
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Link
          to="/changelog"
          onClick={onClose}
          className="mr-auto text-sm font-medium text-primary hover:underline"
        >
          View full changelog
        </Link>
        {onReload ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Later
            </Button>
            <Button onClick={onReload}>Reload to update</Button>
          </>
        ) : (
          <Button onClick={onClose}>Got it</Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
